import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import scoringService from '@/services/scoringService.ts';
import { criterionLabel, criterionDescription } from '@/types/scoring.ts';
import type { ScoringCriterion } from '@/types/scoring.ts';
import { useAlert } from '@/context/AlertContext.tsx';

// Página de settings del Super Admin: peso (0-10) y activación de cada
// criterio que define el scoring del evaluador IA.
export default function SuperUserScoringPage() {
  const { t, i18n } = useTranslation();
  const { showAlert } = useAlert();
  const lang = i18n.resolvedLanguage ?? 'es';

  const [criteria, setCriteria] = useState<ScoringCriterion[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    scoringService
      .getCriteria()
      .then(setCriteria)
      .catch(() => showAlert(t('scoring.loadError'), 'error'))
      .finally(() => setLoading(false));
  }, [showAlert, t]);

  const byCategory = useMemo(() => {
    const groups = new Map<string, ScoringCriterion[]>();
    for (const c of criteria) {
      const list = groups.get(c.category) ?? [];
      list.push(c);
      groups.set(c.category, list);
    }
    return groups;
  }, [criteria]);

  const update = (key: string, patch: Partial<Pick<ScoringCriterion, 'weight' | 'enabled'>>) => {
    setCriteria((prev) => prev.map((c) => (c.key === key ? { ...c, ...patch } : c)));
    setDirty(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await scoringService.updateCriteria(
        criteria.map((c) => ({ key: c.key, weight: c.weight, enabled: c.enabled })),
      );
      setCriteria(updated);
      setDirty(false);
      showAlert(t('scoring.saved'), 'success');
    } catch {
      showAlert(t('scoring.saveError'), 'error');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-32 text-muted-foreground">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-3xl animate-fade-in">
      <div className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading text-foreground">{t('scoring.pageTitle')}</h1>
          <p className="mt-2 text-muted-foreground">{t('scoring.pageSubtitle')}</p>
        </div>
        <Button onClick={save} disabled={!dirty || saving}>
          {saving ? <Loader2 className="size-4 animate-spin" /> : <Save className="size-4" />}
          {t('scoring.save')}
        </Button>
      </div>

      <div className="space-y-8">
        {[...byCategory.entries()].map(([category, items]) => (
          <section key={category}>
            <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
              {t(`scoring.category.${category}`, category)}
            </h2>
            <div className="space-y-2">
              {items.map((c) => (
                <div
                  key={c.key}
                  className={`flex flex-col gap-3 rounded-xl border border-border bg-card p-4 sm:flex-row sm:items-center ${
                    c.enabled ? '' : 'opacity-60'
                  }`}
                >
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-foreground">
                      {criterionLabel(c, lang)}
                      <span className="ml-2 text-xs font-normal text-muted-foreground">{c.key}</span>
                    </p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {criterionDescription(c, lang)}
                    </p>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2">
                      <input
                        type="range"
                        min={0}
                        max={10}
                        value={c.weight}
                        disabled={!c.enabled}
                        onChange={(e) => update(c.key, { weight: Number(e.target.value) })}
                        className="w-32 accent-[var(--primary)]"
                        aria-label={`${criterionLabel(c, lang)} — ${t('scoring.weight')}`}
                      />
                      <span className="w-6 text-center text-sm font-bold text-primary">{c.weight}</span>
                    </div>
                    <Switch
                      checked={c.enabled}
                      onCheckedChange={(checked) => update(c.key, { enabled: checked })}
                      aria-label={`${criterionLabel(c, lang)} — ${t('scoring.enabled')}`}
                    />
                  </div>
                </div>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
